<template>
    <div class="effect-list">
        <image-effects-properties-list-item
            v-if="pinnedItem"
            :id="0"
            :key="pinnedItem.id"
            :item="pinnedItem"
            :readonly="disabled"
            :pinned="true"
            @update="handleUpdate"
            @remove="handleRemove"
        />

        <vue-draggable
            :list="sortable.items"
            :animation="300"
            :disabled="disabled"
            ghost-class="effect-list__ghost"
            chosen-class="effect-list__chosen"
            drag-class="effect-list__drag"
            @change="handleReorder"
        >
            <template v-for="(element, index) in sortable.items">
                <image-effects-properties-list-item
                    :id="index"
                    :key="element.id"
                    :item="element"
                    :readonly="disabled"
                    @update="handleUpdate"
                    @remove="handleRemove"
                />
            </template>
        </vue-draggable>

        <div v-if="items.length === 0" class="effect-list__empty">
            <p>No effects to display</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import VueDraggable from 'vuedraggable';
import { ImageEffectType, isTextToImageEffect } from '@/typings/customilyElements/imageEffectType';
import ImageEffectsPropertiesListItem from '@/components/DesignStudio/ImageEffectsProperties/components/list/item/ImageEffectsPropertiesListItem.vue';

interface Emits {
    (event: 'update', effect: ImageEffectType): void;
    (event: 'remove', effect: ImageEffectType): void;
    (event: 'reorder', effects: ImageEffectType[]): void;
}

const props = defineProps<{
    items: ImageEffectType[];
    disabled?: boolean;
}>();

const emit = defineEmits<Emits>();

const pinnedItem = computed(() =>
    props.items.length && isTextToImageEffect(props.items[0].type) ? props.items[0] : null
);

const sortable = reactive({ items: [] as ImageEffectType[] });

watch(
    () => props.items,
    (items) => {
        const hasPinned = items.length > 0 && isTextToImageEffect(items[0].type);
        sortable.items = hasPinned ? items.slice(1) : [...items];
    },
    { deep: true, immediate: true }
);

const handleUpdate = (imageEffect: ImageEffectType) => {
    emit('update', imageEffect);
};

const handleRemove = (imageEffect: ImageEffectType) => {
    emit('remove', imageEffect);
};

const handleReorder = () => {
    const reordered = pinnedItem.value ? [pinnedItem.value, ...sortable.items] : sortable.items;
    emit('reorder', reordered);
};
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.effect-list {
    position: relative;

    &__empty {
        padding: 2rem;
        text-align: center;
        color: get-color('gray');
        font-style: italic;

        p {
            margin: 0;
        }
    }

    // Dragging states
    &__ghost {
        opacity: 0.5;
        background-color: get-color('primary-lightest');
        transform: rotate(2deg);
    }

    &__chosen {
        background-color: get-color('primary-lightest');
    }

    &__drag {
        transform: rotate(5deg);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        background-color: white;
        border: 1px solid get-color('primary');
    }
}

// Accessibility improvements
@media (prefers-reduced-motion: reduce) {
    .effect-list {
        :deep(.sortable-drag) {
            transform: none !important;
        }

        &__ghost,
        &__chosen,
        &__drag {
            transform: none !important;
        }
    }
}
</style>
